import { useState, useRef } from 'react';
import { SiMeta, SiTiktok, SiSnapchat } from 'react-icons/si';
import { FaLinkedin } from 'react-icons/fa6';
import { Play, Image as ImageIcon, Upload } from 'lucide-react';
import { MetaMockup } from '../../components/mockups/MetaMockup';
import { MetaStoryMockup } from '../../components/mockups/MetaStoryMockup';
import { MetaStoryCarouselMockup } from '../../components/mockups/MetaStoryCarouselMockup';
import { MetaCarouselMockup } from '../../components/mockups/MetaCarouselMockup';
import { MetaCollectionMockup } from '../../components/mockups/MetaCollectionMockup';
import { MetaStoryCollectionMockup } from '../../components/mockups/MetaStoryCollectionMockup';
import { TikTokMockup } from '../../components/mockups/TikTokMockup';
import { SnapchatMockup } from '../../components/mockups/SnapchatMockup';
import { SnapchatStoryAdMockup } from '../../components/mockups/SnapchatStoryAdMockup';
import { LinkedInMockup } from '../../components/mockups/LinkedInMockup';
import { LinkedInCarouselMockup } from '../../components/mockups/LinkedInCarouselMockup';
import { LinkedInDocumentMockup } from '../../components/mockups/LinkedInDocumentMockup';
import { MetaReelsMockup } from '../../components/mockups/MetaReelsMockup';
import { VideoControls } from '../../components/mockups/VideoControls';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { PageHeader } from '@/components/layout/PageContainer';
import { PAGE_PADDING } from '@/components/layout/layoutConstants';
import type { IconType } from 'react-icons';

interface MediaItem {
  id: string;
  url: string;
  type: 'image' | 'video';
  name: string;
  custom?: boolean;
}

interface SampleMediaItem extends MediaItem {
  aspectRatio: '1:1' | '9:16';
}

const SAMPLE_MEDIA: SampleMediaItem[] = [
  {
    id: 'sample-img-1-1-a',
    url: '/mockup-samples/image-1-1-a.jpg',
    type: 'image',
    name: 'Image A',
    aspectRatio: '1:1',
  },
  {
    id: 'sample-img-1-1-b',
    url: '/mockup-samples/image-1-1-b.jpg',
    type: 'image',
    name: 'Image B',
    aspectRatio: '1:1',
  },
  {
    id: 'sample-img-1-1-c',
    url: '/mockup-samples/image-1-1-c.jpg',
    type: 'image',
    name: 'Image C',
    aspectRatio: '1:1',
  },
  {
    id: 'sample-vid-1-1-a',
    url: '/mockup-samples/video-1-1-a.mp4',
    type: 'video',
    name: 'Video A',
    aspectRatio: '1:1',
  },
  {
    id: 'sample-vid-1-1-b',
    url: '/mockup-samples/video-1-1-b.mp4',
    type: 'video',
    name: 'Video B',
    aspectRatio: '1:1',
  },
  {
    id: 'sample-vid-1-1-c',
    url: '/mockup-samples/video-1-1-c.mp4',
    type: 'video',
    name: 'Video C',
    aspectRatio: '1:1',
  },
  {
    id: 'sample-img-9-16-a',
    url: '/mockup-samples/image-9-16-a.jpg',
    type: 'image',
    name: 'Image A',
    aspectRatio: '9:16',
  },
  {
    id: 'sample-img-9-16-b',
    url: '/mockup-samples/image-9-16-b.jpg',
    type: 'image',
    name: 'Image B',
    aspectRatio: '9:16',
  },
  {
    id: 'sample-img-9-16-c',
    url: '/mockup-samples/image-9-16-c.jpg',
    type: 'image',
    name: 'Image C',
    aspectRatio: '9:16',
  },
  {
    id: 'sample-vid-9-16-a',
    url: '/mockup-samples/video-9-16-a.mp4',
    type: 'video',
    name: 'Video A',
    aspectRatio: '9:16',
  },
  {
    id: 'sample-vid-9-16-b',
    url: '/mockup-samples/video-9-16-b.mp4',
    type: 'video',
    name: 'Video B',
    aspectRatio: '9:16',
  },
  {
    id: 'sample-vid-9-16-c',
    url: '/mockup-samples/video-9-16-c.mp4',
    type: 'video',
    name: 'Video C',
    aspectRatio: '9:16',
  },
];

const SAMPLES_1x1 = SAMPLE_MEDIA.filter((s) => s.aspectRatio === '1:1');
const SAMPLES_9x16 = SAMPLE_MEDIA.filter((s) => s.aspectRatio === '9:16');

type PlatformType =
  | 'linkedin'
  | 'linkedin-carousel'
  | 'linkedin-document'
  | 'tiktok'
  | 'snapchat'
  | 'snapchat-story-ad'
  | 'meta-1x1'
  | 'meta-carousel'
  | 'meta-collection'
  | 'meta-9x16'
  | 'meta-9x16-reels'
  | 'meta-9x16-carousel'
  | 'meta-9x16-collection';

type PlatformFamily = 'linkedin' | 'meta' | 'tiktok' | 'snapchat';

type FormatVisual =
  | 'single-square'
  | 'single-portrait'
  | 'carousel-square'
  | 'carousel-portrait'
  | 'collection-square'
  | 'collection-portrait'
  | 'document'
  | 'reels';

interface PlatformFormat {
  id: PlatformType;
  label: string;
  ratio: string;
  visual: FormatVisual;
}

interface FamilyConfig {
  label: string;
  icon: IconType;
  color: string;
  platforms: PlatformFormat[];
}

const FAMILIES: Record<PlatformFamily, FamilyConfig> = {
  linkedin: {
    label: 'LinkedIn',
    icon: FaLinkedin,
    color: '#0A66C2',
    platforms: [
      { id: 'linkedin', label: 'Single', ratio: '1:1', visual: 'single-square' },
      { id: 'linkedin-carousel', label: 'Carousel', ratio: '1:1', visual: 'carousel-square' },
      { id: 'linkedin-document', label: 'Document', ratio: 'Doc', visual: 'document' },
    ],
  },
  meta: {
    label: 'Meta',
    icon: SiMeta,
    color: '#0082FB',
    platforms: [
      { id: 'meta-1x1', label: 'Feed', ratio: '1:1', visual: 'single-square' },
      { id: 'meta-carousel', label: 'Carousel', ratio: '1:1', visual: 'carousel-square' },
      { id: 'meta-9x16', label: 'Story', ratio: '9:16', visual: 'single-portrait' },
      { id: 'meta-9x16-carousel', label: 'Carousel', ratio: '9:16', visual: 'carousel-portrait' },
      { id: 'meta-9x16-reels', label: 'Reels', ratio: '9:16', visual: 'reels' },
      { id: 'meta-collection', label: 'Collection', ratio: '1:1', visual: 'collection-square' },
      {
        id: 'meta-9x16-collection',
        label: 'Collection',
        ratio: '9:16',
        visual: 'collection-portrait',
      },
    ],
  },
  tiktok: {
    label: 'TikTok',
    icon: SiTiktok,
    color: '#000000',
    platforms: [{ id: 'tiktok', label: 'Video', ratio: '9:16', visual: 'reels' }],
  },
  snapchat: {
    label: 'Snapchat',
    icon: SiSnapchat,
    color: '#FFFC00',
    platforms: [
      { id: 'snapchat', label: 'Single', ratio: '9:16', visual: 'single-portrait' },
      { id: 'snapchat-story-ad', label: 'Story Ad', ratio: '9:16', visual: 'carousel-portrait' },
    ],
  },
};

const FAMILY_ORDER: PlatformFamily[] = ['linkedin', 'meta', 'tiktok', 'snapchat'];

function getFamilyForPlatform(p: PlatformType): PlatformFamily {
  if (p.startsWith('linkedin')) return 'linkedin';
  if (p.startsWith('meta')) return 'meta';
  if (p === 'tiktok') return 'tiktok';
  return 'snapchat';
}

function isVideoFile(file: File): boolean {
  return file.type.startsWith('video/');
}

function FormatPreview({ visual }: { visual: FormatVisual }) {
  const card = 'bg-white rounded-md ring-1 ring-slate-300 flex items-center justify-center';
  if (visual === 'single-square')
    return (
      <div className={`w-14 h-14 ${card}`}>
        <ImageIcon className="w-5 h-5 text-slate-300" />
      </div>
    );
  if (visual === 'single-portrait')
    return (
      <div className={`w-9 h-14 ${card}`}>
        <ImageIcon className="w-4 h-4 text-slate-300" />
      </div>
    );
  if (visual === 'reels')
    return (
      <div className={`w-9 h-14 ${card}`}>
        <Play className="w-4 h-4 text-slate-300 fill-slate-300" />
      </div>
    );
  if (visual === 'document')
    return (
      <div className={`w-10 h-14 ${card} flex-col gap-1.5 px-2`}>
        <div className="w-full h-1 bg-slate-200 rounded-full" />
        <div className="w-full h-1 bg-slate-200 rounded-full" />
        <div className="w-3/4 h-1 bg-slate-200 rounded-full" />
      </div>
    );
  if (visual === 'carousel-square')
    return (
      <div className="relative w-16 h-14">
        <div className="absolute right-0 top-1.5 w-12 h-11 bg-white/60 rounded-md ring-1 ring-slate-300" />
        <div className={`absolute left-0 top-0 w-12 h-11 ${card} gap-1`}>
          <div className="w-1.5 h-1.5 rounded-full bg-slate-900" />
          <div className="w-1.5 h-1.5 rounded-full bg-slate-200" />
          <div className="w-1.5 h-1.5 rounded-full bg-slate-200" />
        </div>
      </div>
    );
  if (visual === 'carousel-portrait')
    return (
      <div className="relative w-12 h-14">
        <div className="absolute right-0 top-1.5 w-7 h-12 bg-white/60 rounded-md ring-1 ring-slate-300" />
        <div className={`absolute left-0 top-0 w-7 h-12 ${card} flex-col gap-1`}>
          <div className="w-1.5 h-1.5 rounded-full bg-slate-900" />
          <div className="w-1.5 h-1.5 rounded-full bg-slate-200" />
        </div>
      </div>
    );
  if (visual === 'collection-square')
    return (
      <div className="space-y-1 w-14">
        <div className="w-full h-8 bg-white rounded-md ring-1 ring-slate-300 flex items-center justify-center">
          <ImageIcon className="w-3 h-3 text-slate-300" />
        </div>
        <div className="flex gap-1">
          <div className="flex-1 h-5 bg-white rounded shadow-sm" />
          <div className="flex-1 h-5 bg-white rounded shadow-sm" />
        </div>
      </div>
    );
  if (visual === 'collection-portrait')
    return (
      <div className="flex gap-1 h-14">
        <div className="w-7 h-full bg-white rounded-md ring-1 ring-slate-300 flex items-center justify-center">
          <ImageIcon className="w-3 h-3 text-slate-300" />
        </div>
        <div className="flex flex-col gap-1 flex-1">
          <div className="flex-1 bg-white rounded shadow-sm" />
          <div className="flex-1 bg-white rounded shadow-sm" />
        </div>
      </div>
    );
  return null;
}

export function MockupsPage() {
  const [platform, setPlatform] = useState<PlatformType>('meta-1x1');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [customMedia, setCustomMedia] = useState<MediaItem[]>([]);
  const [mediaTab, setMediaTab] = useState<'library' | 'custom'>('custom');
  const [isDragging, setIsDragging] = useState(false);
  const [currentCarouselSlide, setCurrentCarouselSlide] = useState(0);

  const currentFamily = getFamilyForPlatform(platform);
  const activeFamily = FAMILIES[currentFamily];

  const selectFamily = (family: PlatformFamily) => {
    if (family !== currentFamily) setPlatform(FAMILIES[family].platforms[0].id);
  };

  // Unified media pool: samples + custom uploads
  const getAllMedia = (): MediaItem[] => [...SAMPLE_MEDIA, ...customMedia];

  const getActiveMedia = (): MediaItem[] => {
    const all = getAllMedia();
    return selectedIds
      .map((id) => all.find((m) => m.id === id))
      .filter((m): m is MediaItem => m !== undefined);
  };

  const getSelectionOrder = (id: string): number => {
    const idx = selectedIds.indexOf(id);
    return idx >= 0 ? idx + 1 : 0;
  };

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) => {
      const idx = prev.indexOf(id);
      return idx >= 0 ? prev.filter((i) => i !== id) : [...prev, id];
    });
  };

  const moveToFront = (id: string) => {
    setSelectedIds((prev) => [id, ...prev.filter((i) => i !== id)]);
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const mockupContainerRef = useRef<HTMLDivElement>(null);

  const hasVideoContent = (): boolean => getActiveMedia().some((item) => item.type === 'video');

  const processFiles = (files: File[]) => {
    files
      .filter((f) => f.type.startsWith('image/') || f.type.startsWith('video/'))
      .forEach((file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const newId = `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          const newItem: MediaItem = {
            id: newId,
            url: reader.result as string,
            type: isVideoFile(file) ? 'video' : 'image',
            name: file.name,
            custom: true,
          };
          setCustomMedia((prev) => [...prev, newItem]);
          setSelectedIds((prev) => [...prev, newId]);
        };
        reader.readAsDataURL(file);
      });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) processFiles(Array.from(e.target.files));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    processFiles(Array.from(e.dataTransfer.files));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const removeCustomItem = (id: string) => {
    setCustomMedia((prev) => prev.filter((item) => item.id !== id));
    setSelectedIds((prev) => prev.filter((i) => i !== id));
  };

  const getSelectedMedia = (index: number): string | undefined => getActiveMedia()[index]?.url;

  const getCarouselItems = () => {
    const media = getActiveMedia().slice(0, 5);
    if (media.length === 0) {
      return [1, 2, 3].map((n) => ({
        imageUrl: undefined,
        headline: `Slide ${n}`,
        description: `Description for slide ${n}`,
      }));
    }
    return media.map((item, idx) => ({
      imageUrl: item.url,
      headline: `Slide ${idx + 1}`,
      description: `Description for slide ${idx + 1}`,
    }));
  };

  const getProductItems = () => {
    const media = getActiveMedia().slice(1, 4);
    if (media.length === 0) {
      return [1, 2, 3].map((n) => ({
        imageUrl: undefined,
        title: `Product ${n}`,
        price: `$${n * 10},000`,
      }));
    }
    return media.map((item, idx) => ({
      imageUrl: item.url,
      title: `Product ${idx + 1}`,
      price: `$${(idx + 1) * 10},000`,
    }));
  };

  const [formData, setFormData] = useState({
    pageName: 'Ford Motor Company',
    username: 'fordmotorcompany',
    caption: 'Primary text placeholder\u2026',
    headline: 'Headline placeholder\u2026',
    description: 'Description placeholder\u2026',
    tagline: 'Tagline placeholder\u2026',
    website: 'ford.com',
    ctaText: 'Learn More',
  });

  const inputClass =
    'w-full px-3.5 py-2.5 text-sm rounded-lg border-2 border-transparent bg-slate-50 focus:outline-none focus:border-[#0014cc] focus:bg-white transition-all duration-150';

  // Reusable thumbnail
  const MediaThumb = ({
    item,
    size = 'square',
    fill = false,
  }: {
    item: MediaItem;
    size?: 'square' | 'portrait';
    fill?: boolean;
  }) => {
    const order = getSelectionOrder(item.id);
    const isSelected = order > 0;
    const dims = fill
      ? size === 'portrait'
        ? 'w-full aspect-[9/16]'
        : 'w-full aspect-square'
      : size === 'portrait'
        ? 'w-14 h-24'
        : 'w-20 h-20';
    return (
      <div
        className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all group ${fill ? 'w-full' : 'shrink-0'} ${
          isSelected ? 'border-[#0014cc]' : 'border-slate-200 hover:border-slate-400'
        }`}
        onClick={() => toggleSelection(item.id)}
      >
        <div className={`${dims} bg-slate-100`}>
          {item.type === 'video' ? (
            <video src={item.url} className="w-full h-full object-cover" />
          ) : (
            <img src={item.url} alt={item.name} className="w-full h-full object-cover" />
          )}
        </div>
        {/* Video indicator */}
        {item.type === 'video' && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 pointer-events-none">
            <Play className="w-3 h-3 text-white fill-white drop-shadow" />
          </div>
        )}
        {/* Selection order badge */}
        {isSelected && (
          <div className="absolute top-0.5 right-0.5 w-4 h-4 rounded bg-[#0014cc] text-white text-[9px] font-bold flex items-center justify-center leading-none">
            {order}
          </div>
        )}
        {isSelected && order > 1 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              moveToFront(item.id);
            }}
            className="absolute bottom-0.5 left-0.5 w-4 h-4 rounded bg-slate-700 text-white text-[9px] font-bold opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
            title="Move to first"
          >
            1
          </button>
        )}
        {item.custom && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              removeCustomItem(item.id);
            }}
            className="absolute top-0.5 left-0.5 w-4 h-4 rounded bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-xs leading-none"
          >
            ×
          </button>
        )}
      </div>
    );
  };

  // Family tabs — right-aligned in breadcrumb
  const familyTabs = (
    <div className="flex items-center gap-1 ml-auto">
      {FAMILY_ORDER.map((familyId) => {
        const family = FAMILIES[familyId];
        const Icon = family.icon;
        const isActive = currentFamily === familyId;
        return (
          <button
            key={familyId}
            onClick={() => selectFamily(familyId)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors shrink-0 ${
              isActive
                ? 'bg-slate-100 text-slate-800'
                : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Icon
              className="w-4 h-4 shrink-0"
              style={{ color: isActive ? family.color : undefined }}
            />
            {family.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Sticky header */}
      <PageHeader className="shrink-0">
        <Breadcrumb actions={familyTabs} />
      </PageHeader>

      {/* Main layout — two columns */}
      <div className={`flex gap-4 ${PAGE_PADDING} overflow-hidden min-h-0 flex-1`}>
        {/* LEFT 1/2: Preview only */}
        <div className="w-1/2 shrink-0 min-h-0 flex flex-col items-center justify-center overflow-auto p-4">
          <div
            ref={mockupContainerRef}
            className="flex items-center justify-center w-full [filter:drop-shadow(0px_20px_48px_rgba(0,0,0,0.28))_drop-shadow(0px_4px_12px_rgba(0,0,0,0.16))]"
          >
            {platform === 'linkedin' && (
              <LinkedInMockup
                pageName={formData.pageName}
                caption={formData.caption}
                imageUrl={getSelectedMedia(0)}
                tagline={formData.tagline}
                website={formData.website}
                ctaText={formData.ctaText}
              />
            )}
            {platform === 'linkedin-carousel' && (
              <LinkedInCarouselMockup
                pageName={formData.pageName}
                caption={formData.caption}
                items={getCarouselItems()}
                ctaText={formData.ctaText}
              />
            )}
            {platform === 'linkedin-document' && (
              <LinkedInDocumentMockup
                pageName={formData.pageName}
                caption={formData.caption}
                headline={formData.headline || 'Document Title.pdf'}
                pageImages={getActiveMedia().map((m) => m.url)}
              />
            )}
            {platform === 'meta-1x1' && (
              <MetaMockup
                pageName={formData.pageName}
                caption={formData.caption}
                imageUrl={getSelectedMedia(0)}
                headline={formData.headline}
                description={formData.description}
                ctaText={formData.ctaText}
              />
            )}
            {platform === 'meta-carousel' && (
              <MetaCarouselMockup
                pageName={formData.pageName}
                caption={formData.caption}
                items={getCarouselItems()}
                ctaText={formData.ctaText}
              />
            )}
            {platform === 'meta-collection' && (
              <MetaCollectionMockup
                pageName={formData.pageName}
                caption={formData.caption}
                heroImageUrl={getSelectedMedia(0)}
                products={getProductItems()}
                ctaText={formData.ctaText}
              />
            )}
            {platform === 'meta-9x16' && (
              <div style={{ width: '360px', height: '640px' }}>
                <MetaStoryMockup
                  username={formData.username}
                  caption={formData.caption}
                  imageUrl={getSelectedMedia(0)}
                  ctaText={formData.ctaText}
                  websiteUrl={formData.website}
                />
              </div>
            )}
            {platform === 'meta-9x16-carousel' && (
              <div style={{ width: '360px', height: '640px' }}>
                <MetaStoryCarouselMockup
                  username={formData.username}
                  items={getCarouselItems()}
                  ctaText={formData.ctaText}
                  websiteUrl={formData.website}
                  onSlideChange={setCurrentCarouselSlide}
                />
              </div>
            )}
            {platform === 'meta-9x16-collection' && (
              <div style={{ width: '360px', height: '640px' }}>
                <MetaStoryCollectionMockup
                  username={formData.username}
                  heroImageUrl={getSelectedMedia(0)}
                  products={getProductItems()}
                  ctaText={formData.ctaText}
                  websiteUrl={formData.website}
                />
              </div>
            )}
            {platform === 'meta-9x16-reels' && (
              <MetaReelsMockup
                username={formData.username}
                caption={formData.caption}
                imageUrl={getSelectedMedia(0)}
                ctaText={formData.ctaText}
              />
            )}
            {platform === 'tiktok' && (
              <TikTokMockup
                username={formData.username}
                caption={formData.caption}
                imageUrl={getSelectedMedia(0)}
                ctaText={formData.ctaText}
              />
            )}
            {platform === 'snapchat' && (
              <SnapchatMockup
                username={formData.username}
                imageUrl={getSelectedMedia(0)}
                headline={formData.headline}
              />
            )}
            {platform === 'snapchat-story-ad' && (
              <div style={{ width: '360px', height: '640px' }}>
                <SnapchatStoryAdMockup
                  username={formData.username}
                  items={getCarouselItems()}
                  onSlideChange={setCurrentCarouselSlide}
                />
              </div>
            )}
          </div>
          {hasVideoContent() && (
            <div className="mt-3 w-full max-w-xs">
              <VideoControls
                key={`${platform}-${selectedIds.join(',')}-${currentCarouselSlide}`}
                containerRef={mockupContainerRef}
                show={true}
              />
            </div>
          )}
        </div>
        {/* end left column */}

        {/* RIGHT: Ad Details */}
        <div
          className="flex-1 min-w-0 flex flex-col bg-white/80 backdrop-blur-sm rounded-xl border border-slate-200 overflow-hidden relative"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={() => setIsDragging(false)}
        >
          {isDragging && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl border-2 border-dashed border-[#0014cc] bg-[#0014cc]/5 pointer-events-none">
              <p className="text-sm font-semibold text-[#0014cc]">Drop to upload</p>
            </div>
          )}
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 shrink-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
              Ad Details
            </p>
          </div>
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="p-5 space-y-6">
              {/* Format cards */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2.5">
                  Format
                </label>
                <div className="grid grid-cols-4 gap-2.5">
                  {activeFamily.platforms.map((fmt) => {
                    const isSelected = platform === fmt.id;
                    return (
                      <button
                        key={fmt.id}
                        onClick={() => setPlatform(fmt.id)}
                        className={`flex flex-col rounded-xl border-2 overflow-hidden transition-all text-left ${
                          isSelected
                            ? 'border-[#0014cc] bg-slate-50'
                            : 'border-slate-200 bg-slate-50 hover:border-slate-400'
                        }`}
                      >
                        <div className="flex items-center justify-center p-3 h-24">
                          <FormatPreview visual={fmt.visual} />
                        </div>
                        <div className="px-3 py-2.5 border-t border-slate-100 bg-white flex items-center justify-between gap-1.5">
                          <div className="min-w-0">
                            <p
                              className={`text-xs font-semibold leading-tight truncate ${isSelected ? 'text-[#0014cc]' : 'text-slate-500'}`}
                            >
                              {fmt.label}
                            </p>
                            <p className="text-[11px] text-slate-400 leading-tight">{fmt.ratio}</p>
                          </div>
                          <div
                            className={`w-4 h-4 rounded-full border shrink-0 flex items-center justify-center ${
                              isSelected ? 'border-[#0014cc] bg-[#0014cc]' : 'border-slate-300'
                            }`}
                          >
                            {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Media */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-slate-500">Media</label>
                  <div className="flex items-center bg-slate-100 rounded-md p-0.5">
                    <button
                      onClick={() => setMediaTab('custom')}
                      className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${mediaTab === 'custom' ? 'bg-[#0014cc] text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                      Upload
                    </button>
                    <button
                      onClick={() => setMediaTab('library')}
                      className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${mediaTab === 'library' ? 'bg-[#0014cc] text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                      Library
                    </button>
                  </div>
                </div>

                {/* Upload tab (default) */}
                {mediaTab === 'custom' &&
                  (customMedia.length === 0 ? (
                    <label className="flex flex-col items-center justify-center gap-2.5 w-full h-36 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 cursor-pointer hover:border-[#0014cc] hover:bg-[#dbeafe] transition-all duration-200 group">
                      <Upload className="w-6 h-6 text-slate-300 group-hover:text-[#0014cc] transition-colors duration-200 group-hover:scale-110 transform" />
                      <div className="text-center">
                        <p className="text-sm font-medium text-slate-500 group-hover:text-[#0014cc] transition-colors duration-200">
                          Drag & drop or click to upload
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5">.jpg .png .mp4 .mov</p>
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".jpg,.png,.jpeg,.mp4,.mov"
                        multiple
                        onChange={handleFileInputChange}
                        className="hidden"
                      />
                    </label>
                  ) : (
                    <div className="grid grid-cols-6 gap-1.5">
                      {customMedia.map((item) => (
                        <MediaThumb key={item.id} item={item} size="square" fill />
                      ))}
                      <label className="aspect-square rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:border-[#0014cc] hover:bg-[#0014cc]/5 transition-colors group">
                        <span className="text-2xl leading-none text-slate-400 group-hover:text-[#0014cc]">
                          +
                        </span>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".jpg,.png,.jpeg,.mp4,.mov"
                          multiple
                          onChange={handleFileInputChange}
                          className="hidden"
                        />
                      </label>
                    </div>
                  ))}

                {/* Library tab */}
                {mediaTab === 'library' && (
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">
                        1:1
                      </p>
                      <div className="grid grid-cols-6 gap-1.5">
                        {SAMPLES_1x1.map((item) => (
                          <MediaThumb key={item.id} item={item} size="square" fill />
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">
                        9:16
                      </p>
                      <div className="grid grid-cols-6 gap-1.5">
                        {SAMPLES_9x16.map((item) => (
                          <MediaThumb key={item.id} item={item} size="portrait" fill />
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
                  Page Name
                </label>
                <input
                  type="text"
                  value={formData.pageName}
                  onChange={(e) =>
                    setFormData({ ...formData, pageName: e.target.value, username: e.target.value })
                  }
                  className={inputClass}
                  placeholder="Brand name"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
                  Caption
                </label>
                <textarea
                  value={formData.caption}
                  onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
                  maxLength={3000}
                  rows={4}
                  className={`${inputClass} resize-none`}
                  placeholder="Write your ad caption..."
                />
              </div>

              {(platform === 'meta-1x1' || platform === 'linkedin') && (
                <>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
                      Headline
                    </label>
                    <input
                      type="text"
                      value={formData.headline}
                      onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
                      className={inputClass}
                      placeholder="Headline"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
                      Description
                    </label>
                    <input
                      type="text"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className={inputClass}
                      placeholder="Description"
                    />
                  </div>
                  {platform === 'linkedin' && (
                    <>
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
                          Tagline
                        </label>
                        <input
                          type="text"
                          value={formData.tagline}
                          onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                          className={inputClass}
                          placeholder="Tagline"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
                          Website
                        </label>
                        <input
                          type="text"
                          value={formData.website}
                          onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                          className={inputClass}
                          placeholder="example.com"
                        />
                      </div>
                    </>
                  )}
                </>
              )}

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
                  CTA Button
                </label>
                <select
                  value={formData.ctaText}
                  onChange={(e) => setFormData({ ...formData, ctaText: e.target.value })}
                  className={inputClass}
                >
                  <option>Learn More</option>
                  <option>Shop Now</option>
                  <option>Sign Up</option>
                  <option>Download</option>
                  <option>Apply</option>
                  <option>Get Quote</option>
                  <option>Contact Us</option>
                  <option>Watch Now</option>
                  <option>Get Offer</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
